import { useCallback, useEffect, useState } from "react";
import { logError } from "@/lib/logError";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, FlaskConical, Layers, ScanLine, PenLine, ShieldCheck, Download, Brain } from "lucide-react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PrioriteBadge, StatutBadge, DelaiBadge } from "@/components/shared";
import { REPORT_WRITE, STATUT_ORDER, STATUT_OPTIONNELS, CREATE_CLINICAL } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DictationButton } from "@/components/DictationButton";
import { PrescripteurSelect, usePrescripteurs } from "@/components/PrescripteurSelect";
import { InvitePrescripteurDialog } from "@/components/InvitePrescripteurDialog";
import { RelanceInvitation } from "@/components/RelanceInvitation";
import { RequeteCard } from "@/components/RequeteCard";
import { EtiquettesButtons } from "@/components/EtiquettesButtons";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUTS = STATUT_ORDER;
const SECTIONS = [
  ["partie_b", "sec_b"], ["partie_c", "sec_c"], ["partie_d", "sec_d"],
  ["partie_e", "sec_e"], ["partie_f", "sec_f"], ["partie_h", "sec_h"],
];

function fmtDuree(sec) {
  if (sec == null) return "—";
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  if (h >= 24) { const d = Math.floor(h / 24); return `${d} j ${h % 24} h`; }
  if (h) return `${h} h ${m} min`;
  if (m) return `${m} min`;
  return `${sec} s`;
}

function PhasesTable({ phases, t }) {
  if (!phases || phases.length === 0) return null;
  return (
    <div className="mb-6" data-testid="phases-table">
      <h3 className="text-sm font-semibold text-slate-700 mb-2">{t("dd_phases_title")}</h3>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr><th className="text-left px-3 py-2">{t("dd_phase")}</th><th className="text-left px-3 py-2">{t("dd_by")}</th><th className="text-left px-3 py-2">{t("dd_duree")}</th></tr>
          </thead>
          <tbody>
            {phases.map((p) => (
              <tr key={p.statut} className="border-t border-slate-100" data-testid={`phase-row-${p.statut}`}>
                <td className="px-3 py-2 font-medium text-slate-700">{t(`statut_${p.statut}`)}</td>
                <td className="px-3 py-2 text-slate-500">{p.par || "—"}</td>
                <td className="px-3 py-2">{p.en_cours ? <span className="text-sky-600">{fmtDuree(p.duree_secondes)} ({t("dd_en_cours")})</span> : fmtDuree(p.duree_secondes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Parcours({ statut, t }) {
  const idx = STATUT_ORDER.indexOf(statut);
  return (
    <div className="mb-6 overflow-x-auto pb-2" data-testid="parcours-stepper">
      <div className="flex items-center gap-1 min-w-max">
        {STATUT_ORDER.map((s, i) => {
          const done = idx >= 0 && i < idx;
          const current = i === idx;
          const opt = STATUT_OPTIONNELS.includes(s);
          return (
            <div key={s} className="flex items-center">
              <div className={`px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap ${current ? "bg-sky-600 text-white border-sky-600 shadow-sm" : done ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-slate-400 border-slate-200"} ${opt ? "italic" : ""}`}>
                {t(`statut_${s}`)}{opt ? ` (${t("dd_opt")})` : ""}
              </div>
              {i < STATUT_ORDER.length - 1 && <div className={`h-px w-4 ${done ? "bg-emerald-300" : "bg-slate-200"}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DemandeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [cr, setCr] = useState({});
  const [modeles, setModeles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [prelForm, setPrelForm] = useState({ type_tissu: "", localisation: "" });
  const [addForm, setAddForm] = useState({ texte: "", inclure_ia: true });
  const canReport = REPORT_WRITE.includes(user?.role);
  const canWorkflow = ["technicien", "chef_service", "pathologiste"].includes(user?.role);
  const canLink = CREATE_CLINICAL.includes(user?.role);
  const canWorkflowOrCreate = canWorkflow || canLink;
  const prescripteurs = usePrescripteurs(canLink);
  const linkPresc = async (c) => {
    try {
      await api.patch(`/demandes/${id}/prescripteur`, c ? { prescripteur_id: c.id } : { prescripteur: d.prescripteur, prescripteur_courriel: d.prescripteur_courriel });
      toast.success(c ? `Dossier relié au compte de ${c.nom}` : "Compte prescripteur détaché");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = useCallback(() => api.get(`/demandes/${id}`).then((r) => setD(r.data)).catch((e) => { if (e.response?.status === 404 || e.response?.status === 403) setNotFound(true); }), [id]);
  const loadCr = useCallback(() => api.get(`/demandes/${id}/compte-rendu`).then((r) => setCr(r.data)).catch(logError("Chargement du rapport")), [id]);
  useEffect(() => { load(); loadCr(); api.get("/modeles-compte-rendu").then((r) => setModeles(r.data)).catch(logError("Chargement des modèles")); }, [load, loadCr]);

  const setStatut = async (statut) => {
    try { await api.patch(`/demandes/${id}/statut`, { statut }); toast.success(t("dd_statut_updated")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addPrel = async () => {
    if (!prelForm.type_tissu) return toast.error(t("dd_tissu_req"));
    try { await api.post("/prelevements", { demande_id: id, ...prelForm }); setPrelForm({ type_tissu: "", localisation: "" }); toast.success(t("dd_prel_added")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addBloc = async (prelId) => {
    try { await api.post("/blocs", { prelevement_id: prelId, nb_fragments: 1 }); toast.success(t("dd_bloc_created")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addLame = async (blocId, coloration) => {
    try { await api.post("/lames", { bloc_id: blocId, coloration }); toast.success(t("dd_lame_created")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveCr = async () => {
    try {
      const body = {}; SECTIONS.forEach(([k]) => (body[k] = cr[k] || ""));
      body.integrer_ia = cr.integrer_ia !== false;
      const r = await api.put(`/demandes/${id}/compte-rendu`, body); setCr(r.data); toast.success(t("dd_cr_saved")); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const finalize = async () => {
    try { await api.post(`/demandes/${id}/compte-rendu/finaliser`); toast.success(t("dd_cr_finalized")); loadCr(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addAddendum = async () => {
    try {
      await api.post(`/demandes/${id}/compte-rendu/addendum`, addForm);
      setAddForm({ texte: "", inclure_ia: true }); toast.success(t("dd_add_added")); loadCr();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const applyTemplate = (tpl) => { setCr((prev) => ({ ...prev, ...tpl.sections })); toast.success(t("dd_tpl_applied").replace("{n}", tpl.nom)); };
  const saveAsTemplate = async () => {
    const nom = window.prompt(t("dd_prompt_name")); if (!nom) return;
    const type = window.prompt(t("dd_prompt_type")) || d.type_examen || "";
    const sections = {}; SECTIONS.forEach(([k]) => { sections[k] = cr[k] || ""; });
    try { await api.post("/modeles-compte-rendu", { nom, type_examen: type, sections }); toast.success(t("dd_template_saved")); api.get("/modeles-compte-rendu").then((r) => setModeles(r.data)).catch(() => {}); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (notFound) return <div className="text-slate-500" data-testid="demande-not-found">Demande introuvable ou accès non autorisé.</div>;
  if (!d) return <div className="text-slate-400">{t("dd_loading")}</div>;
  const finalise = cr?.statut === "finalise";
  const reportExists = cr && cr.statut && cr.statut !== "inexistant";

  return (
    <div data-testid="demande-detail-page">
      <button onClick={() => navigate("/demandes")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-3">
        <ArrowLeft className="h-4 w-4" /> {t("dd_back")}
      </button>

      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-head text-2xl font-bold text-slate-900 font-mono">{d.numero}</h1>
            <PrioriteBadge value={d.priorite} /><StatutBadge value={d.statut} />
            <DelaiBadge value={d.delai} cible={d.cible_label} />
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {d.patient ? (d.patient.nom ? `${d.patient.nom} ${d.patient.prenom || ""} · ${d.patient.ramq || ""}` : "Patient anonymisé") : ""} — {d.type_examen}
          </p>
        </div>
        {canWorkflow && (
          <div className="flex items-center gap-2">
            <Label className="text-xs">{t("dd_statut")}</Label>
            <Select value={d.statut} onValueChange={setStatut}>
              <SelectTrigger className="w-48" data-testid="statut-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {STATUTS.map((s) => <SelectItem key={s} value={s}>{t(`statut_${s}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Parcours statut={d.statut} t={t} />
      <PhasesTable phases={d.phases} t={t} />

      <Tabs defaultValue="workflow">
        <TabsList>
          <TabsTrigger value="workflow" data-testid="tab-workflow">{t("dd_tab_workflow")}</TabsTrigger>
          <TabsTrigger value="cr" data-testid="tab-compte-rendu">{t("dd_tab_cr")}</TabsTrigger>
          <TabsTrigger value="info" data-testid="tab-info">{t("dd_tab_info")}</TabsTrigger>
        </TabsList>

        <TabsContent value="workflow" className="mt-4 space-y-4">
          {canWorkflow && (
            <Card className="p-4 border-slate-200 flex items-end gap-3">
              <div className="flex-1"><Label>{t("dd_type_tissu")}</Label>
                <Input data-testid="prel-type-input" value={prelForm.type_tissu} onChange={(e) => setPrelForm({ ...prelForm, type_tissu: e.target.value })} placeholder={t("dd_tissu_ph")} /></div>
              <div className="flex-1"><Label>{t("dd_localisation")}</Label>
                <Input value={prelForm.localisation} onChange={(e) => setPrelForm({ ...prelForm, localisation: e.target.value })} /></div>
              <Button onClick={addPrel} data-testid="add-prel-button" style={{ background: "#0D9488" }}>
                <Plus className="h-4 w-4 mr-1" /> {t("dd_prel")}</Button>
            </Card>
          )}
          {(d.prelevements || []).length > 0 && canWorkflowOrCreate && <EtiquettesButtons demande={d} />}
          {(d.prelevements || []).map((p) => (
            <Card key={p.id} className="p-4 border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-medium text-slate-800">
                  <FlaskConical className="h-4 w-4 text-teal-600" />
                  <span className="font-mono text-sm">{p.numero}</span>
                  <span className="text-slate-500 text-sm">· {p.type_tissu} {p.localisation && `(${p.localisation})`}</span>
                </div>
                {canWorkflow && <Button size="sm" variant="outline" onClick={() => addBloc(p.id)} data-testid={`add-bloc-${p.numero}`}><Plus className="h-3 w-3 mr-1" />{t("dd_bloc")}</Button>}
              </div>
              <div className="mt-3 pl-6 space-y-2">
                {(d.blocs || []).filter((b) => b.prelevement_id === p.id).map((b) => (
                  <div key={b.id} className="border-l-2 border-slate-200 pl-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm"><Layers className="h-3.5 w-3.5 text-slate-500" />
                        <span className="font-mono">{b.numero}</span><Badge variant="outline" className="text-[10px]">{b.nb_fragments} {t("dd_frag")}</Badge></div>
                      {canWorkflow && (
                        <div className="flex gap-1">
                          {["H&E", "IHC", "PAS"].map((col) => (
                            <Button key={col} size="sm" variant="ghost" className="h-6 text-xs" onClick={() => addLame(b.id, col)} data-testid={`add-lame-${b.numero}-${col}`}>+ {col}</Button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1 pl-5">
                      {(d.lames || []).filter((l) => l.bloc_id === b.id).map((l) => (
                        <Badge key={l.id} variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 font-mono text-[10px]">
                          <ScanLine className="h-3 w-3 mr-1" />{l.numero} · {l.coloration}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
          {(d.prelevements || []).length === 0 && <p className="text-sm text-slate-400">{t("dd_no_prel")}</p>}
        </TabsContent>

        <TabsContent value="cr" className="mt-4">
          <Card className="p-5 border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <PenLine className="h-4 w-4 text-sky-600" />
                <h2 className="font-head text-lg font-semibold text-slate-800">{t("dd_cr_title")}</h2>
                {finalise && <Badge className="bg-emerald-600">{t("dd_finalized")}</Badge>}
              </div>
              <div className="flex gap-2">
                {reportExists && (
                  <Button variant="outline" data-testid="download-report-pdf-button"
                          onClick={() => downloadFile(`/demandes/${id}/compte-rendu/pdf`, `CR_${d.numero}.pdf`).catch(() => toast.error(t("dd_pdf_err")))}>
                    <Download className="h-4 w-4 mr-1" /> {t("dd_download_pdf")}
                  </Button>
                )}
                {canReport && !finalise && (
                  <>
                    <label className="flex items-center gap-1.5 text-xs text-slate-600 mr-2 select-none" title={t("dd_integrate_ia_title")}>
                      <input type="checkbox" data-testid="report-integrer-ia-checkbox"
                             checked={cr.integrer_ia !== false}
                             onChange={(e) => setCr({ ...cr, integrer_ia: e.target.checked })} />
                      {t("dd_integrate_ia")}
                    </label>
                    <Button variant="outline" onClick={saveCr} data-testid="save-report-button">{t("dd_save_draft")}</Button>
                    <Button onClick={finalize} data-testid="sign-report-button" style={{ background: "#059669" }}>
                      <ShieldCheck className="h-4 w-4 mr-1" /> {t("dd_finalize")}</Button>
                  </>
                )}
              </div>
            </div>
            {finalise && (
              <p className="text-xs text-slate-500 mb-4">{t("dd_signed_by")} {cr.signataire_nom || cr.signataire} · {cr.signe_le?.slice(0, 16).replace("T", " ")}</p>
            )}
            {canReport && !finalise && modeles.length > 0 && (
              <div className="mb-4 flex items-center gap-2" data-testid="modele-cr-row">
                <Label className="text-xs text-slate-500 whitespace-nowrap">{t("dd_template")}</Label>
                <Select onValueChange={(mid) => { const tpl = modeles.find((m) => m.id === mid); if (tpl) setPreview(tpl); }}>
                  <SelectTrigger className="w-72" data-testid="modele-cr-select"><SelectValue placeholder={t("dd_template_ph")} /></SelectTrigger>
                  <SelectContent className="bg-white">{modeles.map((m) => <SelectItem key={m.id} value={m.id}>{m.nom}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={saveAsTemplate} data-testid="save-as-modele-button">{t("dd_save_template")}</Button>
              </div>
            )}
            <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
              <DialogContent className="bg-white max-w-2xl">
                <DialogHeader><DialogTitle>{t("dd_preview_title")} — {preview?.nom}</DialogTitle></DialogHeader>
                <div className="space-y-3 max-h-[50vh] overflow-y-auto text-sm">
                  {preview && SECTIONS.map(([k, label]) => (preview.sections?.[k] ? (
                    <div key={k}>
                      <div className="font-semibold text-slate-700">{t(label)}</div>
                      <p className="text-slate-600 whitespace-pre-wrap">{preview.sections[k]}</p>
                    </div>
                  ) : null))}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPreview(null)} data-testid="preview-cancel-button">{t("dd_cancel")}</Button>
                  <Button onClick={() => { applyTemplate(preview); setPreview(null); }} data-testid="preview-apply-button" style={{ background: "#0284C7" }}>{t("dd_apply")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {SECTIONS.map(([k, label]) => (
                <div key={k} className={k === "partie_e" ? "xl:col-span-2" : ""}>
                  <Label className="text-slate-700">{t(label)}{k === "partie_e" && " *"}</Label>
                  <Textarea rows={k === "partie_e" ? 3 : 4} disabled={!canReport || finalise}
                            data-testid={`report-${k.replace("_", "-")}-textarea`}
                            value={cr[k] || ""} onChange={(e) => setCr({ ...cr, [k]: e.target.value })} />
                  {canReport && !finalise && (
                    <DictationButton value={cr[k] || ""} onChange={(v) => setCr((c) => ({ ...c, [k]: v }))}
                                     testId={`dictation-${k.replace("_", "-")}`} />
                  )}
                </div>
              ))}
            </div>

            {finalise && (
              <div className="mt-6 border-t border-slate-200 pt-4" data-testid="addenda-section">
                <h3 className="font-head text-sm font-semibold text-slate-800 mb-2">{t("dd_addenda")}</h3>
                {(cr.addenda || []).map((a, i) => (
                  <div key={a.id} className="mb-3 rounded-lg border border-sky-200 bg-slate-50 p-3" data-testid={`addendum-${i}`}>
                    <div className="text-xs font-semibold text-sky-700">{t("dd_addendum")} {i + 1} — {a.signe_le?.slice(0, 16).replace("T", " ")}</div>
                    {a.texte && <p className="text-sm text-slate-600 whitespace-pre-wrap mt-1">{a.texte}</p>}
                    {a.ia_snapshot && (
                      <div className="text-xs text-violet-700 mt-1 flex items-center gap-1">
                        <Brain className="h-3 w-3" /> IA {a.ia_snapshot.modele_nom} v{a.ia_snapshot.version_modele}
                        {a.ia_snapshot.hash8 ? ` · ${a.ia_snapshot.hash8}` : ""}
                      </div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-1">{t("dd_signed_by")} {a.auteur_nom || a.auteur}</div>
                  </div>
                ))}
                {canReport && (
                  <div className="space-y-2" data-testid="addendum-form">
                    <Textarea rows={2} placeholder={t("dd_add_ph")}
                              value={addForm.texte} onChange={(e) => setAddForm({ ...addForm, texte: e.target.value })}
                              data-testid="addendum-texte-textarea" />
                    <DictationButton value={addForm.texte} onChange={(v) => setAddForm((f) => ({ ...f, texte: v }))} testId="dictation-addendum" />
                    <div className="flex items-center gap-3 flex-wrap">
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 select-none">
                        <input type="checkbox" checked={addForm.inclure_ia} data-testid="addendum-inclure-ia-checkbox"
                               onChange={(e) => setAddForm({ ...addForm, inclure_ia: e.target.checked })} />
                        {t("dd_include_ia")}
                      </label>
                      <Button size="sm" onClick={addAddendum} data-testid="add-addendum-button" style={{ background: "#0284C7" }}>
                        <PenLine className="h-4 w-4 mr-1" /> {t("dd_add_addendum")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="info" className="mt-4">
          <Card className="p-5 border-slate-200 text-sm space-y-2">
            <div><span className="text-slate-500">{t("dd_presc")}</span> {d.prescripteur || "—"}</div>
            {canLink && (
              <div className="max-w-md pt-1" data-testid="demande-link-prescripteur">
                <div className="text-slate-500 mb-1">Compte du médecin prescripteur</div>
                <PrescripteurSelect list={prescripteurs} value={d.prescripteur_id} onChange={linkPresc} testId="detail-prescripteur-select" />
                {!d.prescripteur_id && <InvitePrescripteurDialog demande={d} onDone={() => { prescripteurs.reload(); load(); }} />}
                <RelanceInvitation compte={prescripteurs.find((c) => c.id === d.prescripteur_id)} onDone={prescripteurs.reload} />
              </div>
            )}
            <div><span className="text-slate-500">{t("dd_clinique")}</span> {d.clinique || "—"}</div>
            <div><span className="text-slate-500">{t("dd_renseignements")}</span> {d.renseignements_cliniques || "—"}</div>
            <div><span className="text-slate-500">{t("dd_created_by")}</span> {d.created_by} · {d.created_at?.slice(0, 16).replace("T", " ")}</div>
          </Card>
          <RequeteCard d={d} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
